import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import AlertMessage from './components/AlertMessage';
import './App.css';

const App = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [authTokens, setAuthTokens] = useState({
    idToken: '',
    accessToken: '',
    expiresIn: '',
    tokenType: ''
  });
  const [alert, setAlert] = useState({
    show: false,
    type: '',
    message: ''
  });
  const [loading, setLoading] = useState(true);

  // Cognito login URL
  const COGNITO_LOGIN_URL = 'https://infra-automation-login.auth.ap-southeast-2.amazoncognito.com/login?client_id=41142sbsdfutlhi4g36li1cs8i&response_type=token&scope=email+openid+profile&redirect_uri=https%3A%2F%2Finfra.octopusbi.com%2F';

  useEffect(() => {
    // Check for tokens in URL hash when component mounts
    const hash = window.location.hash;
    if (hash) {
      // Parse hash to extract tokens
      const hashParams = hash.substring(1).split('&').reduce((acc, item) => {
        const parts = item.split('=');
        acc[parts[0]] = decodeURIComponent(parts[1]);
        return acc;
      }, {});

      if (hashParams.access_token && hashParams.id_token) {
        const tokenExpiry = new Date();
        tokenExpiry.setSeconds(tokenExpiry.getSeconds() + parseInt(hashParams.expires_in, 10));

        const tokenData = {
          idToken: hashParams.id_token,
          accessToken: hashParams.access_token,
          expiresIn: hashParams.expires_in,
          tokenType: hashParams.token_type || 'Bearer',
          expiryTime: tokenExpiry.getTime()
        };

        setAuthTokens(tokenData);
        setAuthenticated(true);

        // Store tokens in sessionStorage for persistence
        sessionStorage.setItem('authTokens', JSON.stringify(tokenData));

        // Remove the hash from the URL to avoid exposing tokens
        window.history.replaceState({}, document.title, window.location.pathname);

        // Show success message
        showAlert('success', 'Successfully authenticated');
      } else {
        redirectToLogin();
      }
      setLoading(false);
    } else {
      // Check if tokens exist in sessionStorage
      const storedTokens = sessionStorage.getItem('authTokens');
      if (storedTokens) {
        const parsedTokens = JSON.parse(storedTokens);

        // Check if token is expired
        const now = new Date().getTime();
        if (parsedTokens.expiryTime && parsedTokens.expiryTime > now) {
          setAuthTokens(parsedTokens);
          setAuthenticated(true);
        } else {
          // Token expired, clear storage and redirect to login
          sessionStorage.removeItem('authTokens');
          redirectToLogin();
        }
      } else {
        redirectToLogin();
      }
      setLoading(false);
    }
  }, []);

  const redirectToLogin = () => {
    window.location.href = COGNITO_LOGIN_URL;
  };

  const showAlert = (type, message) => {
    setAlert({
      show: true,
      type,
      message
    });

    // Auto hide alert after 3 seconds
    setTimeout(() => {
      setAlert({
        show: false,
        type: '',
        message: ''
      });
    }, 3000);
  };

  const handleLogout = () => {
    // Clear session storage
    sessionStorage.removeItem('authTokens');
    setAuthenticated(false);
    setAuthTokens({
      idToken: '',
      accessToken: '',
      expiresIn: '',
      tokenType: ''
    });
    redirectToLogin();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }

  if (!authenticated) {
    return <Login />;
  }

  return (
    <div className="app-container">
      {alert.show && (
        <AlertMessage
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({...alert, show: false})}
        />
      )}

      <div className="app-header">
        <h1></h1>
        <button className="logout-button" onClick={handleLogout}>
          Logout
        </button>
      </div>

      <Dashboard
        authTokens={authTokens}
        showAlert={showAlert}
      />
    </div>
  );
};

export default App;